import React from 'react';
import { ArrowLeft, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

interface HowToPlayScreenProps {
  onBack: () => void;
  onGotIt: () => void;
  className?: string;
}

const DEMO_TEAMS = [
  {
    nameKey: 'howToPlay.demoTeamUsa' as const,
    image: '/how-to-play/usa-jersey.png',
    vote: '30%',
  },
  {
    nameKey: 'howToPlay.demoTeamBelgium' as const,
    image: '/how-to-play/belgium-jersey.png',
    vote: '70%',
  },
];

/**
 * Circus Daily How to Play — Figma 10329:27065 (picks/mobile/HTP).
 */
const HowToPlayScreen: React.FC<HowToPlayScreenProps> = ({ onBack, onGotIt, className = '' }) => {
  const { t } = useTranslation();

  const steps = [
    { number: 1, title: t('howToPlay.step1Title'), description: t('howToPlay.step1Description') },
    { number: 2, title: t('howToPlay.step2Title'), description: t('howToPlay.step2Description') },
    { number: 3, title: t('howToPlay.step3Title'), description: t('howToPlay.step3Description') },
    { number: 4, title: t('howToPlay.step4Title'), description: t('howToPlay.step4Description') },
  ];

  return (
    <div
      data-test-id="how-to-play-screen"
      className={`fixed inset-0 flex flex-col bg-surface-page ${className}`}
    >
      <button
        data-test-id="back-btn"
        type="button"
        onClick={onBack}
        className="absolute left-6 top-4 z-10 flex items-center gap-1 text-white"
        aria-label={t('howToPlay.back')}
      >
        <ArrowLeft className="size-6 shrink-0" strokeWidth={1.5} aria-hidden />
        <span className="font-sans text-sm font-semibold leading-4">{t('howToPlay.back')}</span>
      </button>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 pb-4 pt-14">
        <div className="mx-auto flex w-full max-w-md flex-col items-center gap-10">
          <div className="flex w-full flex-col items-center gap-6">
            <img
              data-test-id="how-to-play-logo"
              src="/circus-daily-logo-vert.svg"
              alt={t('welcome.altBrand')}
              width={89}
              height={48}
              className="h-12 w-[89px] shrink-0 object-contain"
            />
            <h1
              data-test-id="title"
              className="text-center font-sans text-2xl font-semibold leading-8 text-white"
            >
              {t('howToPlay.title')}
            </h1>
          </div>

          <div className="flex w-full flex-col gap-8">
            {/* Demo pick card */}
            <div data-test-id="how-to-play-illustration" className="relative w-full pt-[15px]">
              <div className="flex w-full flex-col items-center gap-4 rounded-2xl border border-border bg-surface px-4 pb-4 pt-5">
                <p className="w-full text-center font-sans text-sm font-normal leading-5 text-white">
                  {t('howToPlay.demoQuestion')}
                </p>
                <div className="flex w-full gap-3">
                  {DEMO_TEAMS.map(team => (
                    <div
                      key={team.nameKey}
                      className="relative flex min-w-0 flex-1 flex-col items-center gap-3 rounded-xl bg-surface-muted px-2 pb-3 pt-3"
                    >
                      <div className="absolute left-1.5 top-1.5 flex w-7 flex-col items-center">
                        <User className="size-4 text-white" strokeWidth={1.5} aria-hidden />
                        <span className="w-full text-center font-sans text-xs font-semibold leading-3 text-white">
                          {team.vote}
                        </span>
                      </div>
                      <div className="relative size-20 shrink-0 overflow-hidden rounded-full bg-surface-page">
                        <img
                          src={team.image}
                          alt=""
                          className="absolute bottom-0 left-1/2 size-[90%] -translate-x-1/2 object-contain object-bottom"
                        />
                      </div>
                      <p className="w-full text-center font-sans text-sm font-semibold capitalize leading-4 text-white">
                        {t(team.nameKey)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="absolute left-1/2 top-0 flex size-[30px] -translate-x-1/2 items-center justify-center">
                <div className="flex size-[22px] items-center justify-center rounded-full border-[3px] border-surface-page bg-white">
                  <span className="font-sans text-xs font-semibold leading-3 text-primary">1</span>
                </div>
              </div>
            </div>

            {/* Steps */}
            <div data-test-id="steps-section" className="flex w-full flex-col gap-5">
              {steps.map(step => (
                <div
                  key={step.number}
                  data-test-id={`step-${step.number}`}
                  className="flex w-full flex-col items-center gap-2"
                >
                  <div className="flex items-center justify-center gap-2">
                    <div className="flex size-[18px] shrink-0 items-center justify-center rounded-full bg-primary">
                      <span className="font-sans text-xs font-semibold leading-3 text-white">
                        {step.number}
                      </span>
                    </div>
                    <h3 className="font-sans text-base font-semibold leading-6 text-white">
                      {step.title}
                    </h3>
                  </div>
                  <p className="w-full text-center font-sans text-xs font-normal leading-3 text-white">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="shrink-0 px-6 pb-10 pt-2">
        <Button
          data-test-id="got-it-btn"
          type="button"
          variant="primary"
          shape="pill"
          size="lg"
          onClick={onGotIt}
          className="w-full"
        >
          {t('howToPlay.cta')}
        </Button>
      </div>
    </div>
  );
};

export default HowToPlayScreen;
